import { Box, Typography } from '@material-ui/core';
import { Column } from 'primereact/column';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import API from '../../utils/ApiConfig';
import {
  CHECK_VERSION_DELETE_STATUS,
  CHECK_VERSION_LOCK_STATUS,
  DELETE_BUDGET_VERSION_MEMBER,
  GET_VERSION_MEMBER_INFO,
  LOCK_MEMBER,
} from '../../utils/ApiEndpoints';
import DataTablePM from '../common/DataTablePM/DataTablePM';
import ErrorMessage from '../common/ErrorMessage/ErrorMessage';
import InputSwitchPM from '../common/InputSwitchPM/InputSwitchPM';
import InputTextPM from '../common/InputTextPM/InputTextPM';
import Loader from '../common/Loader/Loader';
import { MuiDialog } from '../common/Mui/Dialog/MuiDialog';
import SuccessMessage from '../common/SuccessMessage/SuccessMessage';

export const VersionLocking = props => {
  const { t } = useTranslation();
  const tc = (e, data) => (data ? t(`version_locking:${e}`, data) : t(`version_locking:${e}`));

  const [isLoading, setIsLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [versionName, setVersionName] = useState('');
  const [placeHolderVersionName, setPlaceHolderVersionName] = useState('');
  const [versions, setVersions] = useState([]);
  const [enrichedVersions, setEnrichedVersions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showHardStopDialog, setShowHardStopDialog] = useState(false);
  const [showWarnDialog, setShowWarnDialog] = useState(false);
  const [versionMemberId, setVersionMemberId] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(null);

  const [event, setEvent] = useState(null);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [showDeleteHardStopDialog, setShowDeleteHardStopDialog] = useState(false);
  const [showDeleteWorkflowWarnDialog, setShowDeleteWorkflowWarnDialog] = useState(false);
  const [showDeleteForecastWarnDialog, setShowDeleteForecastWarnDialog] = useState(false);
  const [showDeleteWorkflowAndForecastWarnDialog, setShowDeleteWorkflowAndForecastWarnDialog] = useState(false);

  const CommonService = props.services.CommonService;
  const { $compile, $scope } = props.services;
  const pageHeaderRef = useRef(null);
  const isRedesignEnabled = () => !!$scope?.$root?.isLegacyThemeRedesignEnabled?.();

  const centageSupportEmail = {
    centageSupportEmail: <a href="mailto:support@centage.com">support@centage.com</a>,
  };

  const lockOpen = <span className="VLCircleIcon material-icons md-24 material-symbols-outlined">lock_open_right</span>;

  const getVersionMemberInfoStatus = planId => API.get(GET_VERSION_MEMBER_INFO(planId));
  const checkVersionDimensionLockStatus = (planId, versionId) => API.get(CHECK_VERSION_LOCK_STATUS(planId, versionId));
  const lockMember = (planId, versionMemberId) =>
    API.post(LOCK_MEMBER, {
      planId,
      versionMemberId,
      ignoreOpenWorkflow: true,
      checkIfItHasCrossVersionDependencies: true,
    });
  const checkVersionDimensionDeleteStatus = (planId, versionId) => API.get(CHECK_VERSION_DELETE_STATUS(planId, versionId));
  const deleteBudgetVersion = (planId, versionMemberId) =>
    API.post(DELETE_BUDGET_VERSION_MEMBER, {
      planId,
      versionMemberId,
    });

  useEffect(() => {
    if (!isRedesignEnabled() || !$compile || !$scope || !pageHeaderRef.current) {
      return;
    }

    $scope.pageHeaderTitle = CommonService.isDeleteBudgetVersionEnabled ? tc('VERSION_MANAGEMENT_HEADER_TITLE') : tc('HEADER_TITLE');
    if (!$scope.$$phase) {
      $scope.$apply();
    }

    const pageHeaderElement = $compile('<lmapp-page-header icon="icon-ql-version" [title]="pageHeaderTitle"></lmapp-page-header>')($scope);
    pageHeaderRef.current.appendChild(pageHeaderElement[0]);

    return () => {
      pageHeaderElement.remove();
    };
  }, []);

  useEffect(() => {
    const planParam = props.pageParams.data.plan;
    getVersionMemberInfoStatus(planParam.id)
      .then(response => {
        setVersions(response.data);
      })
      .finally(() => {
        setIsTableLoading(false);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    enrichVersionsData(versions);
  }, [versions]);

  useEffect(() => {
    const tableData = enrichedVersions.map(ev => {
      const lockStatus = ev.enabled ? (
        CommonService.auth.getCurrentUser().userType === 'ACCOUNTADMIN' && CommonService.isDeleteBudgetVersionEnabled ? (
          <div className="VLActionLocked">
            <span className="VLActionLockedIcon material-icons material-symbols-outlined">lock</span>
            <span className="VLActionDeleteIcon material-icons material-symbols-outlined" onClick={deleteMember(ev.memberId)}>
              delete
            </span>
          </div>
        ) : (
          <div className="VLActionLocked">
            <span className="VLActionLockedIcon material-icons material-symbols-outlined">lock</span>
          </div>
        )
      ) : CommonService.auth.getCurrentUser().userType === 'ACCOUNTADMIN' ? (
        CommonService.isDeleteBudgetVersionEnabled ? (
          <div className="VLActionWrapper">
            {lockOpen}
            <InputSwitchPM name={ev.memberId} checked={ev.enabled} onValueChange={handleSwitchChange(ev.memberId)} className="VLActionUnlocked" />
            <span className="VLActionDeleteIcon material-icons material-symbols-outlined">delete</span>
          </div>
        ) : (
          <div className="VLActionWrapper">
            {lockOpen}
            <InputSwitchPM name={ev.memberId} checked={ev.enabled} onValueChange={handleSwitchChange(ev.memberId)} className="VLActionUnlocked" />
          </div>
        )
      ) : (
        <div className="VLActionWrapper">{lockOpen}</div>
      );
      const statusText = ev.enabled ? 'Locked' : 'Unlocked';
      return { ...ev, versionLockStatus: lockStatus, status: statusText };
    });
    setTableData(tableData);
  }, [enrichedVersions]);

  useEffect(() => {
    const foundRows = tableData.filter(
      row => row.name.toLowerCase().includes(searchText.toLowerCase()) || row.description.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredData(foundRows);
  }, [searchText]);

  const isApplyLockButtonDisabled = useCallback(() => {
    if (enrichedVersions.length > 0) {
      const version = enrichedVersions.find(ev => ev.memberId === versionMemberId);
      return !!version && version.name !== versionName;
    }
  }, [versionMemberId, versionName]);

  const isDeleteButtonDisabled = useCallback(() => {
    if (enrichedVersions.length > 0) {
      const version = enrichedVersions.find(ev => ev.memberId === versionMemberId);
      return !!version && version.name !== versionName;
    }
  }, [versionMemberId, versionName]);

  const handleSwitchChange = useCallback(
    memberId => event => {
      const planParam = props.pageParams.data.plan;
      checkVersionDimensionLockStatus(planParam.id, memberId)
        .then(response => {
          const { usedInOpenWorkFlow, hasCrossVersionDependencies } = { ...response.data };
          if (hasCrossVersionDependencies) {
            setShowHardStopDialog(true);
          } else {
            if (enrichedVersions.length > 0) {
              const version = enrichedVersions.find(ev => ev.memberId === memberId);
              if (version != undefined) {
                setPlaceHolderVersionName(version.name);
              }
            }
            usedInOpenWorkFlow ? setShowWarnDialog(true) : setShowConfirmationDialog(true);
            setVersionMemberId(memberId);
            setEvent(event);
          }
        })
        .catch(err => {
          err && err.response && err.response.data && setErrorMessage(err.response.data.status);
          setShowErrorMessage(true);
        });
    },
    [enrichedVersions]
  );

  const deleteMember = useCallback(
    memberId => event => {
      const planParam = props.pageParams.data.plan;
      checkVersionDimensionDeleteStatus(planParam.id, memberId)
        .then(response => {
          const { hasCrossVersionDependencies, usedInOpenWorkflow, usedInClosedWorkflow, usedInForecastVersion } = { ...response.data };
          if (hasCrossVersionDependencies) {
            setShowDeleteHardStopDialog(true);
          } else {
            if (enrichedVersions.length > 0) {
              const version = enrichedVersions.find(ev => ev.memberId === memberId);
              if (version != undefined) {
                setPlaceHolderVersionName(version.name);
              }
            }
            if ((usedInOpenWorkflow || usedInClosedWorkflow) && usedInForecastVersion) {
              setShowDeleteWorkflowAndForecastWarnDialog(true);
            } else if (usedInOpenWorkflow || usedInClosedWorkflow) {
              setShowDeleteWorkflowWarnDialog(true);
            } else if (usedInForecastVersion) {
              setShowDeleteForecastWarnDialog(true);
            } else {
              setShowDeleteConfirmationDialog(true);
            }
            setVersionMemberId(memberId);
            setEvent(event);
          }
        })
        .catch(err => {
          err && err.response && err.response.data && setErrorMessage(err.response.data.status);
          setShowErrorMessage(true);
        });
    },
    [enrichedVersions]
  );

  const sliderUpdate = event => {
    return enrichedVersions.map(obj => {
      return obj.memberId === versionMemberId ? { ...obj, enabled: event.target.value } : obj;
    });
  };

  const deleteMemberUpdate = () => {
    return enrichedVersions.filter(obj => obj.memberId !== versionMemberId);
  };

  const applyLock = () => {
    const planParam = props.pageParams.data.plan;
    lockMember(planParam.id, versionMemberId)
      .then(response => {
        setEnrichedVersions(sliderUpdate(event));
        setSuccessMessage('VL_SUCCESS');
        setShowSuccessMessage(true);
      })
      .catch(err => {
        if (err && err.response && err.response.data) {
          setErrorMessage(err.response.data.status);
          setShowErrorMessage(true);
        }
      })
      .finally(() => {
        setVersionMemberId('');
        setVersionName('');
        setPlaceHolderVersionName('');
        setShowConfirmationDialog(false);
        setEvent(null);
      });
  };

  const deleteVersion = () => {
    const planParam = props.pageParams.data.plan;
    setIsLoading(true);
    deleteBudgetVersion(planParam.id, versionMemberId)
      .then(response => {
        setEnrichedVersions(deleteMemberUpdate());
        setSuccessMessage('VD_SUCCESS');
        setShowSuccessMessage(true);
      })
      .catch(err => {
        if (err && err.response && err.response.data) {
          setErrorMessage(err.response.data.status);
          setShowErrorMessage(true);
        }
      })
      .finally(() => {
        setVersionMemberId('');
        setVersionName('');
        setPlaceHolderVersionName('');
        setShowDeleteConfirmationDialog(false);
        setEvent(null);
        setIsLoading(false);
      });
  };

  const enrichVersionsData = versionRows => {
    let tempEnrichedVersions = [];
    for (const verRow of versionRows) {
      tempEnrichedVersions.push({
        ...verRow,
        enabled: verRow.versionLockStatus === 'LOCKED',
      });
    }
    setEnrichedVersions(tempEnrichedVersions);
  };

  const closeErrorMessage = () => setShowErrorMessage(false);

  const closeConfirmationDialog = () => {
    setShowConfirmationDialog(false);
    setVersionMemberId('');
    setVersionName('');
    setPlaceHolderVersionName('');
    setEvent(null);
  };

  const closeErrorStopDialog = () => {
    setShowHardStopDialog(false);
  };

  const closeWarnDialog = () => {
    setShowWarnDialog(false);
    setVersionMemberId('');
    setPlaceHolderVersionName('');
    setEvent(null);
  };

  const proceedToDeleteConfirmationDialog = () => {
    setShowDeleteWorkflowWarnDialog(false);
    setShowDeleteWorkflowAndForecastWarnDialog(false);
    setShowDeleteForecastWarnDialog(false);
    setShowDeleteConfirmationDialog(true);
  };

  const closeDeleteConfirmationDialog = () => {
    setShowDeleteConfirmationDialog(false);
    setVersionMemberId('');
    setVersionName('');
    setPlaceHolderVersionName('');
    setEvent(null);
  };

  const closeDeleteErrorStopDialog = () => {
    setShowDeleteHardStopDialog(false);
  };

  const closeDeleteWorkflowWarnDialog = () => {
    setShowDeleteWorkflowWarnDialog(false);
    setVersionMemberId('');
    setPlaceHolderVersionName('');
    setEvent(null);
  };

  const closeDeleteWorkflowAndForecastWarnDialog = () => {
    setShowDeleteWorkflowAndForecastWarnDialog(false);
    setVersionMemberId('');
    setPlaceHolderVersionName('');
    setEvent(null);
  };

  const closeDeleteForecastWarnDialog = () => {
    setShowDeleteForecastWarnDialog(false);
    setVersionMemberId('');
    setPlaceHolderVersionName('');
    setEvent(null);
  };

  const proceedToConfirmationDialog = () => {
    setShowWarnDialog(false);
    setShowConfirmationDialog(true);
  };

  const columnNames = [
    {
      field: 'name',
      header: tc('VL_COLUMN_HEADER_VERSION_NAME'),
      sortField: 'name',
    },
    {
      field: 'description',
      header: tc('VL_COLUMN_HEADER_VERSION_DESCRIPTION'),
      sortField: 'description',
    },
    {
      field: 'versionType',
      header: tc('VL_COLUMN_HEADER_VERSION_TYPE'),
      sortField: 'versionType',
    },
    {
      field: 'status',
      header: tc('VL_COLUMN_HEADER_VERSION_STATUS'),
      sortField: 'status',
    },
    {
      field: 'versionLockStatus',
      header: tc('VL_COLUMN_HEADER_VERSION_ACTION'),
      style: { width: '200px' },
      sortField: 'enabled',
    },
  ];

  const columns = columnNames.map(({ field, header, style, sortField }, index) => {
    if (field === 'versionLockStatus') {
      return <Column key={index} field={field} header={header} style={{ ...style, textAlign: 'center' }} sortable={false} />;
    }
    return <Column key={index} field={field} header={header} style={style} sortField={sortField} sortable={true} />;
  });

  const confirmationDialogTitleContent = () => {
    return (
      <div>
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('VL_CONFIRMATION_POPUP_HEADER')}</Box>
        </Typography>
      </div>
    );
  };

  const confirmationDialogContent = () => {
    return (
      <div>
        <div className="VLPopupText">
          <Trans t={t} i18nKey={tc('VL_CONFIRMATION_POPUP_TEXT')} />
        </div>
        <div style={{ marginBottom: '30px' }}>
          <InputTextPM value={versionName} placeholder={placeHolderVersionName} onChange={setVersionName} className="VLPopupInputText" />
        </div>
        <div className="VLPopupFooterRight">
          <button id={'applyButton'} className="btn btn-primary" disabled={isApplyLockButtonDisabled()} onClick={applyLock}>
            <Box fontWeight="bold">{tc('APPLY_BUTTON')}</Box>
          </button>
          <button id="cancelButton" className="btn btn-outline" disabled={false} onClick={closeConfirmationDialog}>
            <Box fontWeight="bold">{tc('CANCEL_BUTTON')}</Box>
          </button>
        </div>
      </div>
    );
  };

  const errorStopDialogTitleContent = () => {
    return (
      <div className="VLPopupTitleCentered">
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('VL_HARD_STOP_POPUP_HEADER')}</Box>
        </Typography>
      </div>
    );
  };

  const errorStopDialogContent = () => {
    return (
      <div>
        <div className="VLPopupTextCentered">
          <Trans t={t} i18nKey={tc('VL_HARD_STOP_POPUP_TEXT')} components={centageSupportEmail} />
        </div>
        <div className="VLPopupFooterCentered">
          <button id={'closeButton'} className="btn btn-primary" disabled={false} onClick={closeErrorStopDialog}>
            <Box fontWeight="bold">{tc('CLOSE_BUTTON')}</Box>
          </button>
        </div>
      </div>
    );
  };

  const warnDialogTitleContent = () => {
    return (
      <div className="VLPopupTitleCentered">
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('VL_SOFT_WARN_POPUP_HEADER')}</Box>
        </Typography>
      </div>
    );
  };

  const warnDialogContent = () => {
    return (
      <div>
        <div className="VLPopupTextCentered">
          <Trans t={t} i18nKey={tc('VL_SOFT_WARN_POPUP_TEXT')} components={centageSupportEmail} />
        </div>
        <div className="VLPopupFooterCentered">
          <button id={'continueButton'} className="btn btn-primary" disabled={false} onClick={proceedToConfirmationDialog}>
            <Box fontWeight="bold">{tc('CONTINUE_BUTTON')}</Box>
          </button>
          <button id="cancelButton" className="btn btn-outline" disabled={false} onClick={closeWarnDialog}>
            <Box fontWeight="bold">{tc('CANCEL_BUTTON')}</Box>
          </button>
        </div>
      </div>
    );
  };

  const deleteConfirmationDialogTitleContent = () => {
    return (
      <div>
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('VD_CONFIRMATION_POPUP_HEADER')}</Box>
        </Typography>
      </div>
    );
  };

  const deleteConfirmationDialogContent = () => {
    return (
      <div>
        <div className="VLPopupText">
          <Trans t={t} i18nKey={tc('VD_CONFIRMATION_POPUP_TEXT')} />
        </div>
        <div style={{ marginBottom: '30px' }}>
          <InputTextPM value={versionName} placeholder={placeHolderVersionName} onChange={setVersionName} className="VLPopupInputText" />
        </div>
        <div className="VLPopupFooterRight">
          <button id={'deleteButton'} className="btn btn-primary" disabled={isDeleteButtonDisabled()} onClick={deleteVersion}>
            <Box fontWeight="bold">{tc('DELETE_BUTTON')}</Box>
          </button>
          <button id="cancelButton" className="btn btn-outline" disabled={false} onClick={closeDeleteConfirmationDialog}>
            <Box fontWeight="bold">{tc('CANCEL_BUTTON')}</Box>
          </button>
        </div>
      </div>
    );
  };

  const deleteErrorStopDialogTitleContent = () => {
    return (
      <div className="VLPopupTitleCentered">
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('VD_HARD_STOP_POPUP_HEADER')}</Box>
        </Typography>
      </div>
    );
  };

  const deleteErrorStopDialogContent = () => {
    return (
      <div>
        <div className="VLPopupTextCentered">
          <Trans t={t} i18nKey={tc('VD_HARD_STOP_POPUP_TEXT')} components={centageSupportEmail} />
        </div>
        <div className="VLPopupFooterCentered">
          <button id={'closeButton'} className="btn btn-primary" disabled={false} onClick={closeDeleteErrorStopDialog}>
            <Box fontWeight="bold">{tc('CLOSE_BUTTON')}</Box>
          </button>
        </div>
      </div>
    );
  };

  const deleteWorkflowWarnDialogTitleContent = () => {
    return (
      <div className="VLPopupTitleCentered">
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('VD_WORKFLOW_SOFT_WARN_POPUP_HEADER')}</Box>
        </Typography>
      </div>
    );
  };

  const deleteWorkflowWarnDialogContent = () => {
    return (
      <div>
        <div className="VLPopupTextCentered">
          <Trans t={t} i18nKey={tc('VD_WORKFLOW_SOFT_WARN_POPUP_TEXT')} components={centageSupportEmail} />
        </div>
        <div className="VLPopupFooterCentered">
          <button id={'continueButton'} className="btn btn-primary" disabled={false} onClick={proceedToDeleteConfirmationDialog}>
            <Box fontWeight="bold">{tc('CONTINUE_BUTTON')}</Box>
          </button>
          <button id="cancelButton" className="btn btn-outline" disabled={false} onClick={closeDeleteWorkflowWarnDialog}>
            <Box fontWeight="bold">{tc('CANCEL_BUTTON')}</Box>
          </button>
        </div>
      </div>
    );
  };

  const deleteWorkflowAndForecastWarnDialogTitleContent = () => {
    return (
      <div className="VLPopupTitleCentered">
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('VD_WORKFLOW_AND_FORECAST_SOFT_WARN_POPUP_HEADER')}</Box>
        </Typography>
      </div>
    );
  };

  const deleteWorkflowAndForecastWarnDialogContent = () => {
    return (
      <div>
        <div className="VLPopupTextCentered">
          <Trans t={t} i18nKey={tc('VD_WORKFLOW_AND_FORECAST_SOFT_WARN_POPUP_TEXT')} components={centageSupportEmail} />
        </div>
        <div className="VLPopupFooterCentered">
          <button id={'continueButton'} className="btn btn-primary" disabled={false} onClick={proceedToDeleteConfirmationDialog}>
            <Box fontWeight="bold">{tc('CONTINUE_BUTTON')}</Box>
          </button>
          <button id="cancelButton" className="btn btn-outline" disabled={false} onClick={closeDeleteWorkflowAndForecastWarnDialog}>
            <Box fontWeight="bold">{tc('CANCEL_BUTTON')}</Box>
          </button>
        </div>
      </div>
    );
  };

  const deleteForecastWarnDialogTitleContent = () => {
    return (
      <div className="VLPopupTitleCentered">
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('VD_FORECAST_SOFT_WARN_POPUP_HEADER')}</Box>
        </Typography>
      </div>
    );
  };

  const deleteForecastWarnDialogContent = () => {
    return (
      <div>
        <div className="VLPopupTextCentered">
          <Trans t={t} i18nKey={tc('VD_FORECAST_SOFT_WARN_POPUP_TEXT')} components={centageSupportEmail} />
        </div>
        <div className="VLPopupFooterCentered">
          <button id={'continueButton'} className="btn btn-primary" disabled={false} onClick={proceedToDeleteConfirmationDialog}>
            <Box fontWeight="bold">{tc('CONTINUE_BUTTON')}</Box>
          </button>
          <button id="cancelButton" className="btn btn-outline" disabled={false} onClick={closeDeleteForecastWarnDialog}>
            <Box fontWeight="bold">{tc('CANCEL_BUTTON')}</Box>
          </button>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <div className="VersionLocking">
        {isLoading ? <Loader /> : ''}
        {isRedesignEnabled() && <div className="VLPageHeader" ref={pageHeaderRef}></div>}
        <div className="container-fluid" style={isRedesignEnabled() ? { display: 'flex', flexDirection: 'column' } : undefined}>
          {!isRedesignEnabled() && (
            <div className="VLHeader">
              <span className="VLCircleIcon material-icons md-28 material-symbols-outlined">lock</span>
              <h3>{CommonService.isDeleteBudgetVersionEnabled ? tc('VERSION_MANAGEMENT_HEADER_TITLE') : tc('HEADER_TITLE')}</h3>
            </div>
          )}
          <hr />
          <div>
            <InputTextPM value={searchText} placeholder={tc('INPUT_PLACEHOLDER')} onChange={setSearchText} className="VLInputText" />
          </div>
          <div className="VLTable">
            <DataTablePM
              value={searchText ? filteredData : tableData}
              columns={columns}
              loading={isTableLoading}
              resizableColumns={true}
              columnResizeMode="fit"
              scrollable
              sortMode="single"
              className="no-filter"
              selection={selectedVersion}
              selectionMode="single"
              onSelectionChange={e => setSelectedVersion(e.value)}
              dataKey="memberId"
            />
          </div>
        </div>
      </div>
      <MuiDialog
        open={showConfirmationDialog}
        onClose={closeConfirmationDialog}
        dialogId="confirmation-dialog"
        dialogTitleContent={confirmationDialogTitleContent}
        dialogContent={confirmationDialogContent}
      />
      <MuiDialog
        open={showHardStopDialog}
        onClose={closeErrorStopDialog}
        dialogId="error-stop-dialog"
        dialogTitleContent={errorStopDialogTitleContent}
        dialogContent={errorStopDialogContent}
      />

      <MuiDialog
        open={showWarnDialog}
        onClose={closeWarnDialog}
        dialogId="warn-dialog"
        dialogTitleContent={warnDialogTitleContent}
        dialogContent={warnDialogContent}
      />

      <MuiDialog
        open={showDeleteConfirmationDialog}
        onClose={closeDeleteConfirmationDialog}
        dialogId="delete-confirmation-dialog"
        dialogTitleContent={deleteConfirmationDialogTitleContent}
        dialogContent={deleteConfirmationDialogContent}
      />
      <MuiDialog
        open={showDeleteHardStopDialog}
        onClose={closeDeleteErrorStopDialog}
        dialogId="delete-error-stop-dialog"
        dialogTitleContent={deleteErrorStopDialogTitleContent}
        dialogContent={deleteErrorStopDialogContent}
      />

      <MuiDialog
        open={showDeleteWorkflowWarnDialog}
        onClose={closeDeleteWorkflowWarnDialog}
        dialogId="delete-workflow-warn-dialog"
        dialogTitleContent={deleteWorkflowWarnDialogTitleContent}
        dialogContent={deleteWorkflowWarnDialogContent}
      />

      <MuiDialog
        open={showDeleteForecastWarnDialog}
        onClose={closeDeleteForecastWarnDialog}
        dialogId="delete-forecast-warn-dialog"
        dialogTitleContent={deleteForecastWarnDialogTitleContent}
        dialogContent={deleteForecastWarnDialogContent}
      />

      <MuiDialog
        open={showDeleteWorkflowAndForecastWarnDialog}
        onClose={closeDeleteWorkflowAndForecastWarnDialog}
        dialogId="delete-workflow-forecast-warn-dialog"
        dialogTitleContent={deleteWorkflowAndForecastWarnDialogTitleContent}
        dialogContent={deleteWorkflowAndForecastWarnDialogContent}
      />

      <ErrorMessage open={showErrorMessage} handleClose={closeErrorMessage} message={errorMessage} />

      <SuccessMessage open={showSuccessMessage} setOpen={setShowSuccessMessage} message={tc(successMessage)} type="success" />
    </Fragment>
  );
};
